import { DialogHelper } from './dialog.ts';
import { storageManager } from './storage.ts';
import { $currentVersionId } from './state.ts';
import { showNotification } from './notifications.ts';
import type { CsvImportVersionChoice, CsvImportChoice } from './types.ts';

export class CsvVersionChoiceDialog {
  private dialog: DialogHelper;
  private guestCount: number;
  private onConfirm: (choice: CsvImportVersionChoice) => void;

  constructor(guestCount: number, onConfirm: (choice: CsvImportVersionChoice) => void) {
    this.dialog = new DialogHelper();
    this.guestCount = guestCount;
    this.onConfirm = onConfirm;
  }

  show(): void {
    // Check if we have a current version
    const currentVersionId = $currentVersionId.get();

    // Clone template and populate with data
    const template = document.getElementById('csv-import-version-choice-template') as HTMLTemplateElement;
    const content = template.content.cloneNode(true) as DocumentFragment;
    
    // Generate unique IDs to avoid conflicts
    const uniqueId = `csv-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Populate guest count
    const guestCountElement = content.querySelector('.guest-count-placeholder') as HTMLElement;
    guestCountElement.textContent = `${this.guestCount}`;

    // Update IDs and names to be unique
    const newVersionNameField = content.querySelector('#new-version-name-field') as HTMLElement;
    const newVersionNameInput = content.querySelector('#csvImportVersionName') as HTMLInputElement;
    const radioInputs = content.querySelectorAll('.radio-input') as NodeListOf<HTMLInputElement>;
    
    // If no current version, hide the "replace current" option and pre-select "new version"
    if (!currentVersionId) {
      const replaceOption = content.querySelector('input[value="overwrite_current"]')?.closest('.radio-option') as HTMLElement;
      if (replaceOption) {
        replaceOption.style.display = 'none';
      }
      
      // Pre-select "new version" option
      const newVersionRadio = content.querySelector('input[value="new_version"]') as HTMLInputElement;
      if (newVersionRadio) {
        newVersionRadio.checked = true;
      }
    }
    
    // Make IDs unique
    newVersionNameField.id = `new-version-name-field-${uniqueId}`;
    newVersionNameInput.id = `csvImportVersionName-${uniqueId}`;
    
    // Make radio button names unique to this dialog instance
    const radioGroupName = `import-choice-${uniqueId}`;
    radioInputs.forEach(radio => {
      radio.name = radioGroupName;
    });
    
    // Update label for attribute
    const label = content.querySelector('.form-label[for="csvImportVersionName"]') as HTMLLabelElement;
    if (label) {
      label.setAttribute('for', newVersionNameInput.id);
    }

    // Set placeholder text instead of default value to encourage user input
    newVersionNameInput.placeholder = 'Enter a descriptive name for this layout...';

    // Store reference to the element before it's moved to the dialog
    const dialogContent = content.firstElementChild as HTMLElement;

    // Show/hide version name field based on radio selection
    const updateVersionNameField = () => {
      const selectedChoice = dialogContent.querySelector(`input[name="${radioGroupName}"]:checked`) as HTMLInputElement;
      const nameField = dialogContent.querySelector(`#new-version-name-field-${uniqueId}`) as HTMLElement;
      const nameInput = dialogContent.querySelector(`input[id^="csvImportVersionName-"]`) as HTMLInputElement;
      
      if (selectedChoice?.value === 'new_version') {
        nameField.style.display = 'block';
        nameInput.required = true;
      } else {
        nameField.style.display = 'none';
        nameInput.required = false;
      }
    };

    // If no current version, show version name field by default
    if (!currentVersionId) {
      newVersionNameField.style.display = 'block';
      newVersionNameInput.required = true;
    } else {
      // Initial update for when there is a current version
      updateVersionNameField();
    }
    
    const dialogTitle = !currentVersionId ? 'Create Layout Version' : 'CSV Import Options';
    
    this.dialog.show({
      title: dialogTitle,
      content: dialogContent,
      className: 'csv-version-choice-dialog'
    }, [
      {
        text: 'Cancel',
        className: 'modal-btn',
        handler: () => this.dialog.close()
      },
      {
        text: !currentVersionId ? 'Create Version' : 'Continue Import',
        className: 'modal-btn modal-btn-primary',
        primary: true,
        handler: async () => {
          await this.handleConfirm(dialogContent, radioGroupName);
        }
      }
    ]);

    // Add event listeners for radio buttons after dialog is shown
    const dialogRadioInputs = dialogContent.querySelectorAll('.radio-input') as NodeListOf<HTMLInputElement>;
    dialogRadioInputs.forEach(radio => {
      radio.addEventListener('change', updateVersionNameField);
    });
  }

  private async handleConfirm(content: HTMLElement, radioGroupName: string): Promise<void> {
    // Get the selected choice using the unique radio group name
    const selectedRadio = content.querySelector(`input[name="${radioGroupName}"]:checked`) as HTMLInputElement;
    const choice = selectedRadio?.value as CsvImportChoice;
    
    if (!choice) {
      showNotification('Error', 'Please select an import option', 'error');
      return;
    }

    let newVersionName: string | undefined;

    if (choice === 'new_version') {
      // Find the input with the unique ID pattern
      const nameInput = content.querySelector('input[id^="csvImportVersionName-"]') as HTMLInputElement;
      newVersionName = nameInput?.value.trim();
      
      if (!newVersionName) {
        showNotification('Error', 'Please enter a descriptive name for this layout version', 'error');
        return;
      }

      // Check for duplicate names
      const isDuplicate = await storageManager.isVersionNameTaken(newVersionName);
      if (isDuplicate) {
        showNotification('Error', 'A version with this name already exists', 'error');
        return;
      }
    }

    // Close dialog and call confirm callback
    this.dialog.close();
    this.onConfirm({
      choice,
      newVersionName
    });
  }
}

export async function showCsvVersionChoiceDialog(guestCount: number): Promise<CsvImportVersionChoice> {
  return new Promise((resolve) => {
    const dialog = new CsvVersionChoiceDialog(guestCount, resolve);
    dialog.show();
  });
}